import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { X, Tag, PlusCircle } from 'lucide-react'
import { SearchApi } from '@/services/core/searchApi'

interface LabelAutocompleteProps {
  value: string[]
  onChange: (value: string[]) => void
  placeholder?: string
  className?: string
  style?: React.CSSProperties
  /**
   * The searched object type — `Label` for ordinary labels, `MatchingLabel`
   * for the ad unit labels that targeting matches on. They are separate
   * namespaces, so suggestions must come from the one being edited.
   */
  types?: string
}

export function LabelAutocomplete({
  value,
  onChange,
  placeholder,
  style = { width: '100%' },
  types = 'Label',
}: LabelAutocompleteProps) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [showDropdown, setShowDropdown] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const existingRef = useRef<Set<string>>(new Set())
  const abortRef = useRef<AbortController | null>(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const search = useCallback(async (q: string) => {
    const trimmed = q.trim()
    if (!trimmed) {
      setSuggestions([])
      setShowDropdown(false)
      return
    }

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    try {
      const res = await SearchApi.search({ q: trimmed, types, pageSize: 20, orderBy: 'NAME' })
      if (controller.signal.aborted) return

      const valueLower = new Set(value.map((v) => v.toLowerCase()))
      const names = res.searchResults
        .map((r) => r.name)
        .filter((n) => !valueLower.has(n.toLowerCase()))

      existingRef.current = new Set(names.map((n) => n.toLowerCase()))

      const exactMatch = names.some((n) => n.toLowerCase() === trimmed.toLowerCase())
        || valueLower.has(trimmed.toLowerCase())
      if (!exactMatch) {
        names.push(trimmed)
      }

      setSuggestions(names)
      setHighlightIndex(-1)
      setShowDropdown(names.length > 0)
    } catch {
      if (!controller.signal.aborted) {
        existingRef.current = new Set()
        setSuggestions([trimmed])
        setHighlightIndex(-1)
        setShowDropdown(true)
      }
    }
  }, [value, types])

  const handleInputChange = (val: string) => {
    setQuery(val)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => search(val), 300)
  }

  const selectSuggestion = (name: string) => {
    if (!value.some((v) => v.toLowerCase() === name.toLowerCase())) {
      onChange([...value, name])
    }
    setQuery('')
    setSuggestions([])
    setShowDropdown(false)
    setHighlightIndex(-1)
  }

  const removeLabel = (index: number) => {
    onChange(value.filter((_, i) => i !== index))
  }

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setShowDropdown(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlightIndex((prev) => Math.min(prev + 1, suggestions.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlightIndex((prev) => Math.max(prev - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (highlightIndex >= 0 && highlightIndex < suggestions.length) {
        selectSuggestion(suggestions[highlightIndex])
      } else if (query.trim()) {
        selectSuggestion(query.trim())
      }
    } else if (e.key === 'Escape') {
      setShowDropdown(false)
    } else if (e.key === 'Backspace' && !query && value.length > 0) {
      onChange(value.slice(0, -1))
    }
  }

  return (
    <div ref={containerRef} className="relative" style={style}>
      <div className="flex flex-wrap gap-1.5 rounded-(--ad-radius-sm) border border-(--ad-border) bg-(--ad-surface) p-2 min-h-9.5">
        {value.map((label, i) => (
          <span key={i} className="inline-flex items-center gap-1 rounded-full bg-(--ad-badge-blue-bg) text-(--ad-badge-blue) border border-(--ad-badge-blue-border) px-2 py-0.5 text-xs font-medium">
            {label}
            <button type="button" onClick={() => removeLabel(i)} className="hover:text-(--destructive)">
              <X className="size-3" />
            </button>
          </span>
        ))}
        <input
          className="flex-1 min-w-30 bg-transparent outline-none text-sm text-(--ad-text) placeholder:text-(--ad-text-muted)"
          value={query}
          onChange={(e) => handleInputChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { if (suggestions.length > 0) setShowDropdown(true) }}
          placeholder={value.length === 0 ? (placeholder ?? t('labels.placeholder')) : ''}
        />
      </div>
      {showDropdown && suggestions.length > 0 && (
        <div className="absolute z-50 w-full mt-1 border border-(--ad-border) rounded-md bg-(--ad-surface) shadow-md max-h-48 overflow-auto">
          {suggestions.map((name, i) => {
            const isExisting = existingRef.current.has(name.toLowerCase())
            return (
              <div
                key={i}
                className={`flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer text-(--ad-text) hover:bg-(--ad-primary-soft) hover:text-(--ad-primary-strong)${highlightIndex === i ? ' bg-(--ad-primary-soft) text-(--ad-primary-strong)' : ''}`}
                onMouseDown={(e) => { e.preventDefault(); selectSuggestion(name) }}
                onMouseEnter={() => setHighlightIndex(i)}
              >
                {isExisting ? (
                  <Tag className="size-3 text-(--ad-success)" />
                ) : (
                  <PlusCircle className="size-3 text-(--ad-text-muted)" />
                )}
                <span>{name}</span>
                {!isExisting && (
                  <span className="text-xs text-(--ad-text-muted) italic">{t('common.new')}</span>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}
